import { createNotImplementedActor } from '@b2b/common/utils/xstate';
import { assertEvent, enqueueActions, setup } from 'xstate';

export interface OrdersDetailContext {
  orderId: string | null;
  organizationId: string | null;
  can: {
    cancelMembership: boolean;
  };
  onlinePaymentUUID: string;
}

type OrdersDetailEvents =
  | { type: 'SET_CANCEL_MEMBERSHIP_ELIGIBILITY'; params: { orderId: string; canCancelMembership: boolean } }
  | { type: 'FETCH_ONLINE_PAYMENT_LINK'; params: { orderNumber: string; organizationId: string | null } }
  | { type: 'CREATE_ONLINE_PAYMENT_LINK'; params: { orderNumber: string } }
  | { type: 'DELETE_ONLINE_PAYMENT_LINK'; params: { onlinePaymentUUID: string } };

export const ordersDetailMachine = setup({
  types: {
    context: {} as OrdersDetailContext,
    events: {} as OrdersDetailEvents,
    emitted: {} as { type: 'ONLINE_PAYMENT_LINK_CHANGED'; params: { onlinePaymentUUID: string } },
  },

  actions: {
    setCancelMembershipEligibility: enqueueActions(
      ({ enqueue, context }, params: { orderId: string; canCancelMembership: boolean }) => {
        enqueue.assign({
          ...context,
          orderId: params.orderId,
          can: {
            ...context.can,
            cancelMembership: params.canCancelMembership,
          },
        });
      }
    ),
    setOrganizationId: enqueueActions(({ enqueue }, params: { organizationId: string | null }) => {
      enqueue.assign({ organizationId: () => params.organizationId });
    }),
    createOnlinePaymentLink: enqueueActions(({ enqueue }, params: { onlinePaymentUUID: string }) => {
      enqueue.assign({ onlinePaymentUUID: params.onlinePaymentUUID });
      enqueue.emit({ type: 'ONLINE_PAYMENT_LINK_CHANGED', params: { onlinePaymentUUID: params.onlinePaymentUUID } });
    }),
    deleteOnlinePaymentLink: enqueueActions(({ enqueue }) => {
      enqueue.assign({ onlinePaymentUUID: '' });
      enqueue.emit({ type: 'ONLINE_PAYMENT_LINK_CHANGED', params: { onlinePaymentUUID: '' } });
    }),
    setOnlinePaymentLink: enqueueActions(({ enqueue }, params: { onlinePaymentUUID: string }) => {
      enqueue.assign({ onlinePaymentUUID: params.onlinePaymentUUID });
      enqueue.emit({ type: 'ONLINE_PAYMENT_LINK_CHANGED', params: { onlinePaymentUUID: params.onlinePaymentUUID } });
    }),
  },

  actors: {
    fetchPaymentLink: createNotImplementedActor<
      { onlinePaymentUUID: string },
      { orderNumber: string; isOrgOrder: boolean }
    >(),
    createPaymentLink: createNotImplementedActor<{ onlinePaymentUUID: string }, { orderNumber: string }>(),
    deletePaymentLink: createNotImplementedActor<{ onlinePaymentUUID: string }, { onlinePaymentUUID: string }>(),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QHsBOEytgETAFwEMBLAGwGIBlAUQBUB9AYQEEA5BqgGToFkruAhKgCUKACQCSABTqdxAcXH9xHcTQCaAbQAMAXUSgADslhE8RZADt9IAB6IArAEYAHADpnAJi0A2Z84DMAJyBAOz2AQA0IACeiI4eAL4JUWgYWLiEpK4GBNEAtmAWeHQkRBYA1q5EECRgZABitAyidADyLCosVHSSTGq8LPSdANLaekggRiZmltZ2CP72Wq5aACweTo4hId4ha97eUbEIuyGuq1qBHluB9iGhzqtJKeiYOPjEJNm5BUUlZZVqrUyAwhFQmDRuu1Ot1ev0qIM6CMxtYpqZzFYJvN-KtHK5vB4PEEXKtVrtAo5DjFEP4tG5HI4tESgvdnIFvPZniBUm8Mp9vvlCsVShUqjU6thOLQoR1xF0en0BkM5aNdKjjOjZljEDsPOdGWtHo52c5KUcHB5Vq4XH57MEyR4Do4uTz0h8sjlBX8RZUAGb4ADGAAsylA6J7fsKAWQIJYwFULAA3ZDleOu96ZL4RoX-UX+vDB0Phn45n0IMrJgMEGYWMYoiZomtzGm4-GE4mPMn3M3UhCrQJWvxacLsjz3LT+HYu15uzMCyO5v2BkMWMPZ73RzCoNDZEjV31oPKudN8j0ljd55dF9dRirlpPIKs1utqhsapvahatglEimd8k9sc4TeNapJ+IEPh7ASTzJNyM4ZvyN6Lq4AaoGA1bXuet7lDGcYJsmqbHvBp5ZlhyGoehZirsWXrYfelYYZYL7jIY74Ys2X54j+HakgBVLHIsZz+N4Pj2F4E7hM4nKwSe7qkbR5FoRh1FIT6ZBbjuBh7ngB6oEeslzqpAIoUpVFrmRZYVo+jG1ro9asdM7GfjiXHtn+vHdvxiBgW2-ZOKSY6rCEjrTmkCFngpPquBgtRmTRC5qbGFjxlZhEGYhFnGTF+CYZFAL0dZz52a+DmapioDzAE9iuBSY5aD4ax2v45p9v4epXKs4RLEsRIBN4oW8nJ86lllYCxblCWbqg26oLu+6HkRYUkcNF6VNlcVGXeVlPhizHqo5WoVYgVU1fEewNZ1gTNb2Ln4iJHhSd4DJaPc3gwbBFjIBg8ATOlpD7WVHEALTGi1V2uBsFIMi4k5vWJA2zvyACuRAAx+R19iELUuMspKXN4-hbC9tUwS8S1DZt5Ro05GMeIELWEssVwBKawQPd4gTOAj4XyZNopAmA1OHbYiAHP45zBE4V0UqshP2C1mw1asjyPE1QVMtJZODYZmWXgWK7mXlFRC+VIsII85yy3cnhMqSbXXccHIgesjj2ATjhBdssvc8tlMmZRE0jcbb4Hab8zbC1Fx4o4ix3MrdoMvcPsU7ra1jTlKmpybHGMgSriE5alJaDHwRea17UhM4ImnP4bVx0kSRAA */
  id: 'ordersDetail',

  context: () => {
    return {
      orderId: null,
      organizationId: null,
      onlinePaymentUUID: '',
      can: {
        cancelMembership: false,
      },
    };
  },

  type: 'parallel',

  on: {
    SET_CANCEL_MEMBERSHIP_ELIGIBILITY: {
      actions: { type: 'setCancelMembershipEligibility', params: ({ event }) => event.params },
    },
  },

  states: {
    ui: {},

    payment_link: {
      initial: 'idle',

      states: {
        idle: {
          on: {
            FETCH_ONLINE_PAYMENT_LINK: {
              target: 'fetching_payment_link',
              actions: { type: 'setOrganizationId', params: ({ event }) => event.params },
            },
            CREATE_ONLINE_PAYMENT_LINK: { target: 'creating_payment_link' },
            DELETE_ONLINE_PAYMENT_LINK: { target: 'deleting_payment_link' },
          },
        },

        fetching_payment_link: {
          invoke: {
            src: 'fetchPaymentLink',
            input: ({ event }) => {
              assertEvent(event, 'FETCH_ONLINE_PAYMENT_LINK');

              return {
                orderNumber: event.params.orderNumber,
                isOrgOrder: !!event.params.organizationId,
              };
            },
            onDone: {
              target: 'idle',
              actions: {
                type: 'setOnlinePaymentLink',
                params: ({ event }) => {
                  return { onlinePaymentUUID: event.output.onlinePaymentUUID };
                },
              },
            },
            onError: {
              target: 'idle',
              actions: {
                type: 'setOnlinePaymentLink',
                params: () => ({ onlinePaymentUUID: '' }),
              },
            },
          },
          onError: { target: 'idle' },
        },

        creating_payment_link: {
          invoke: {
            src: 'createPaymentLink',
            input: ({ event }) => {
              assertEvent(event, 'CREATE_ONLINE_PAYMENT_LINK');

              return { orderNumber: event.params.orderNumber };
            },
            onDone: {
              target: 'idle',
              actions: {
                type: 'createOnlinePaymentLink',
                params: ({ event }) => {
                  return { onlinePaymentUUID: event.output.onlinePaymentUUID };
                },
              },
            },
            onError: { target: 'idle' },
          },
        },

        deleting_payment_link: {
          invoke: {
            src: 'deletePaymentLink',
            input: ({ event }) => {
              assertEvent(event, 'DELETE_ONLINE_PAYMENT_LINK');
              return { onlinePaymentUUID: event.params.onlinePaymentUUID };
            },
            onDone: {
              target: 'idle',
              actions: { type: 'deleteOnlinePaymentLink' },
            },
            onError: {
              target: 'idle',
            },
          },
        },
      },
    },
  },
});
